import { supabase } from "../lib/supabase";

/**
 * Path format definitions:
 * - Legacy format: {user_id}/{booking_id}/{timestamp}_{filename}
 * - New format: {company_id}/{booking_id}/{timestamp}_{filename}
 */

interface PathComponents {
  rootId: string;  // Either user_id or company_id
  bookingId: string;
  fileName: string;
  isCompanyPath: boolean;
}

/**
 * Generate a file path for commercial invoice using company ID as the root folder
 */
export const generateCompanyBasedPath = (
  companyId: number | string,
  bookingId: number | string,
  fileName: string
): string => {
  // Sanitize the filename
  const sanitizedFileName = sanitizeFileName(fileName);
  
  // Create path with company_id as root
  return `${companyId}/${bookingId}/${Date.now()}_${sanitizedFileName}`;
};

/**
 * Generate a file path for commercial invoice using user ID as the root folder (legacy format)
 */
export const generateUserBasedPath = (
  userId: string,
  bookingId: number | string,
  fileName: string
): string => {
  // Sanitize the filename
  const sanitizedFileName = sanitizeFileName(fileName);
  
  // Create path with user_id as root (legacy format)
  return `${userId}/${bookingId}/${Date.now()}_${sanitizedFileName}`;
};

/**
 * Sanitize a filename to remove special characters, spaces, etc.
 */
export const sanitizeFileName = (fileName: string): string => {
  return fileName
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/[^\w.-]/g, "") // Remove non-alphanumeric characters except dots and hyphens
    .normalize("NFD") // Normalize accented characters
    .replace(/[\u0300-\u036f]/g, ""); // Remove diacritics
};

/**
 * Parse a file path to extract its components
 */
export const parseFilePath = (path: string): PathComponents | null => {
  if (!path) return null;
  
  const parts = path.split('/');
  
  // Path must have at least 3 parts: rootId/bookingId/filename
  if (parts.length < 3) return null;
  
  const rootId = parts[0];
  const bookingId = parts[1];
  const fileName = parts.slice(2).join('/'); // In case filename contains forward slashes
  
  // If rootId is numeric and has no dashes, consider it a companyId
  // User IDs from Supabase auth are typically UUIDs with dashes
  const isCompanyPath = /^\d+$/.test(rootId);
  
  return {
    rootId,
    bookingId,
    fileName,
    isCompanyPath
  };
};

/**
 * Get a public URL for a commercial invoice
 * Tries the provided path first, if it fails and tryAlternateFormat is true,
 * it will attempt to determine if the path is in the old or new format and try the other
 */
export const getCommercialInvoicePublicUrl = (
  path: string,
  tryAlternateFormat: boolean = false
): string | null => {
  if (!path) return null;

  try {
    // Try the provided path first
    const { data } = supabase.storage
      .from("commercial_invoices")
      .getPublicUrl(path);
    
    return data.publicUrl;
  } catch (error) {
    if (!tryAlternateFormat) return null;
    
    // If we should try alternate format and the first attempt failed,
    // this is where we would implement the logic to try the alternate path format
    // However, getPublicUrl doesn't typically throw errors for invalid paths,
    // so this branch is unlikely to be reached
    console.error("Error getting public URL:", error);
    return null;
  }
};

/**
 * Create a signed URL for a commercial invoice
 * This will attempt to use the provided path, and if that fails and tryAlternateFormat is true,
 * it will attempt to determine if the path is user-based or company-based and try the other format
 */
export const createCommercialInvoiceSignedUrl = async (
  path: string,
  expiresIn: number = 60,
  tryAlternateFormat: boolean = false
): Promise<string | null> => {
  if (!path) return null;

  try {
    // First attempt with provided path
    const { data, error } = await supabase.storage
      .from("commercial_invoices")
      .createSignedUrl(path, expiresIn);
    
    if (!error && data) {
      return data.signedUrl;
    }
    
    // If we need to try alternate format and first attempt failed
    if (tryAlternateFormat) {
      console.log("Primary path failed, will try to determine alternate format");
      // Implement alternate path logic if needed in the future
      // For now, just log the error since we don't have enough context
      // to determine what the alternate path would be without additional information
      console.error("Error creating signed URL with primary path:", error);
    }
    
    return null;
  } catch (error) {
    console.error("Error creating signed URL:", error);
    return null;
  }
};

/**
 * Upload a commercial invoice file using the company-based path structure
 */
export const uploadCommercialInvoiceWithCompanyPath = async (
  file: File,
  companyId: number | string,
  bookingId: number | string
): Promise<string | null> => {
  try {
    // Generate path with company ID as root
    const filePath = generateCompanyBasedPath(companyId, bookingId, file.name);
    
    console.log("Uploading commercial invoice with company path:", filePath);
    
    // Upload to Supabase storage
    const { data, error } = await supabase.storage
      .from("commercial_invoices")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });
    
    if (error) {
      console.error("Error uploading commercial invoice:", error);
      return null;
    }
    
    console.log("Commercial invoice uploaded successfully:", data.path);
    return data.path;
  } catch (error) {
    console.error("Error in uploadCommercialInvoiceWithCompanyPath:", error);
    return null;
  }
};

/**
 * Download a commercial invoice file
 */
export const downloadCommercialInvoice = async (
  path: string,
  filename: string
): Promise<void> => {
  try {
    if (!path || path.trim() === '') {
      throw new Error("Invalid file path provided");
    }
    
    // Clean the file path - remove any leading slash
    const cleanPath = path.trim().replace(/^\//, "");
    
    console.log("Downloading commercial invoice:", cleanPath);
    
    // Get the file extension
    const fileExt = cleanPath.split(".").pop()?.toLowerCase() || "pdf";
    
    // Try to create a signed URL with a longer expiration time
    const { data, error } = await supabase.storage
      .from("commercial_invoices")
      .createSignedUrl(cleanPath, 3600, {
        download: true,
      });
    
    if (error) {
      console.error("Error creating signed URL for download:", error);
      throw error;
    }
    
    if (data?.signedUrl) {
      console.log("Successfully generated signed URL for download");
      // Create a temporary link and trigger download
      const link = document.createElement("a");
      link.href = data.signedUrl;
      link.download = `${filename}.${fileExt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log("Download triggered for:", filename);
    } else {
      throw new Error("Could not generate download link");
    }
  } catch (error) {
    console.error("Error downloading commercial invoice:", error);
    throw error;
  }
}; 